import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { uuid } from 'uuidv4';
import { BorderedWell } from '../../../../components/bordered-well/BorderedWell';
import { Button } from '../../../../components/button/Button';
import { TextInput, ToggleInput } from '../../../../components/form-elements/_index';
import { PageBody, PageContent } from '../../../../components/page-components/_index';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { UsersSvc } from '../../../../shared/services/users-service';
import { StatusCode } from '../../../../utils/api';
import { UserSettingEntry, UserSettings, UserSettingsEntryTopic } from '../../../../support/models/users/user-settings';

export const NotificationSettingsScene = () => {

    const [service] = React.useState(new UsersSvc());
    const [settings, setSettings] = React.useState<UserSettings>(new UserSettings());
    const [topics, setTopics] = React.useState<UserSettingEntry[]>([]);
    const [pushText, setPushText] = React.useState('');
    const [pushTopic, setPushTopic] = React.useState('');

    /**
     * Helper method to fetch the user's subscriptions and setup the page
     * for editing
     */
    async function getSettings() {
        const resp = await service.getUserNotifications();
        if (resp.status === StatusCode.Success) {
            setupTopics(resp.data);
            setSettings(resp.data);
        }
    }
    
    /**
     * Initialize the page with the user's subscriptions
     */
    React.useEffect(() => {
        getSettings();        
    }, []);

    /**
     * A helper method to convert the server response to a list of possible subscriptions the 
     * user can register for
     * @param resp A UserSettings object listing the topics the current user is subscribed to
     */
    const setupTopics = (resp : UserSettings) => {

        const isInUserList = (findMe: string) => {
            return resp?.topics.indexOf(findMe) === -1 ? false : true;
        };

        const l = [
            {
                id: uuid(),
                display: 'Production',
                topics: [
                    { 
                        display: 'Run Update', 
                        description: 'Whenever a production run gets updated in the system, this alert will be generated.', 
                        value: 'production.runupdate', 
                        enabled: isInUserList('production.runupdate') 
                    },
                    { 
                        display: 'Task Update', 
                        description: 'Whenever a production task gets updated in the system, this alert will be generated.',
                        value: 'production.taskupdate',  
                        enabled: isInUserList('production.taskupdate')
                    }
                ]
            },
            {
                id: uuid(),
                display: 'System',
                topics: [
                    { 
                        display: 'Downtime', 
                        description: 'When Enterprise is about to undergo scheduled downtime, this alert will be generated.',
                        value: 'system.downtime', 
                        enabled: isInUserList('system.downtime')
                    }
                ]
            }
        ];

        setTopics(l);
    };

    /**
     * Toggle the enabled flag on the given topic
     * @param sectionId The unique ID for the UI section
     * @param topic The topic value to toggle
     */
    const toggleTopicSubscription = (sectionId: string, topic : string) => {
        const t = [...topics];

        t.forEach((itm : UserSettingEntry) => {
            if (itm.id === sectionId) {
                itm.topics.forEach((tItm: UserSettingsEntryTopic) => {
                    if (tItm.value === topic) {
                        tItm.enabled = !tItm.enabled;
                    }
                });
            }
        });

        setTopics(t);
    };

    /**
     * Update the user's subscriptions in the backend
     */
    const updateSubscriptions = async () => {
        const resp = await service.updateUserNotifications(topics);
        if (resp.status === StatusCode.Success) {
            await getSettings();
        }
    };

    const sendTestNotification = async () => {
        await service.testPushNotification(pushText.trim(), pushTopic.trim());
        setPushText('');
        setPushTopic('');
    };

    const onPushTextChange = (e: any) => {
        setPushText(e.target.value);        
    };

    const onPushTopicChange = (e: any) => {
        setPushTopic(e.target.value);        
    };
    
    return <PageContent pageName={'NotificationSettings'}>
            <PageBody classes="user-notifications">

                <div className="row">
                    <div className="col">
                        <h2>Manage Your Alerts</h2>
                        <p>
                            Enterprise sends alerts for a wide variety of actions that occur throughout the system.  
                            By subscribing to an alert below, you will receive updates for those actions in the notifications panel in Enterprise.  
                            (These alerts are access via the 'bell' in the top toolbar.)
                        </p>
                    </div>                       
                </div>

                <HasPermission requiredPermissions={[
                    Permissions.SupportUsersSave
                ]}>
                    <div className="row mt-5 mb-5">
                        <div className="col">
                            <BorderedWell>
                                <h4>ADMINS ONLY</h4>                    
                                <p>The raw topic list from the server is as follows:</p>
                                <code>
                                    {
                                        settings.topics.length > 0 
                                            ? settings.topics.reduce((curr: string, next: string) => {
                                                return curr + '"' + next + '" ';
                                            }, '') 
                                            : 'Nothing was returned...' 
                                    } 
                                </code>  

                                <p className="mt-5">Send Dummy Notification.</p>
                                <p>(If you don't supply a topic, the push will be sent to your user.)</p>
                                <TextInput onChange={ onPushTextChange } label="Push Body Text (Required)" type="text" value={ pushText } id="push-text-input" />
                                <TextInput onChange={ onPushTopicChange } label="Push Topic (Optional)" type="text" value={ pushTopic } id="push-text-input" />
                                <button className="btn" onClick={ sendTestNotification }>Send A Test</button>
                            </BorderedWell>
                        </div>

                    </div>
                </HasPermission>

                {
                    topics.map((itm : UserSettingEntry) => 
                        <React.Fragment key={itm.id}>
                            <div className="row">
                                <div className="col">
                            <h2 className='subscription-section-header'>
                                { itm.display }
                                
                            </h2>
                            </div>
                            </div>
                            <div className="row">
                                {
                                    itm.topics.map((topicItm: UserSettingsEntryTopic) => 
                                        <div 
                                            className="col-6" 
                                            key={`toggle-${topicItm.value}`}>
                                            <ToggleInput
                                                id={`toggle-input-${topicItm.value}`}
                                                label={topicItm.display}
                                                name={`toggle-input-${topicItm.value}`}
                                                onClick={ () => toggleTopicSubscription(itm.id, topicItm.value) }
                                                checked={ topicItm.enabled } 
                                            />

                                            <p>{ topicItm.description }</p>
                                                
                                        </div>
                                    )
                                }
                            </div>
                        </React.Fragment>
                    )
                }

                <div className="row justify-content-center mt-5">
                    <div className="col-auto">
                        <Button
                            testID="update-topic-subscriptions"
                            onBtnClick={ updateSubscriptions }
                            text="Update Preferences"
                            id="update-topic-subscriptions"
                        />                            
                    </div>
                </div>
                
            </PageBody>
        </PageContent>;
    
};
